import type { DashboardResolveWidgetLine } from "@t3tools/contracts";
import { useCallback, useEffect, useRef, useState } from "react";

/** How long changed widgets stay highlighted. */
export const EDIT_HIGHLIGHT_MS = 2_400;

const NO_WIDGETS: ReadonlySet<string> = new Set();
const NO_PREVIEWS: ReadonlyMap<string, DashboardResolveWidgetLine> = new Map();

/**
 * What the assistant is doing to the dashboard on screen: widgets it just
 * changed (highlighted briefly) and data it is previewing before saving. A
 * preview is a whole resolve line, so it carries its provenance and freshness.
 */
export function useDashboardLiveEdits() {
  const [highlighted, setHighlighted] = useState<ReadonlySet<string>>(NO_WIDGETS);
  const [previews, setPreviews] =
    useState<ReadonlyMap<string, DashboardResolveWidgetLine>>(NO_PREVIEWS);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const highlight = useCallback((widgetIds: ReadonlySet<string>) => {
    window.clearTimeout(timer.current);
    setHighlighted(widgetIds);
    timer.current = window.setTimeout(() => setHighlighted(NO_WIDGETS), EDIT_HIGHLIGHT_MS);
  }, []);

  const preview = useCallback((widgetId: string, line: DashboardResolveWidgetLine) => {
    setPreviews((current) => new Map(current).set(widgetId, line));
  }, []);

  /** Drops the previews of `widgetIds`, or all of them. */
  const clearPreviews = useCallback((widgetIds?: ReadonlySet<string>) => {
    setPreviews((current) => {
      if (!widgetIds) return current.size ? NO_PREVIEWS : current;
      const next = new Map([...current].filter(([id]) => !widgetIds.has(id)));
      return next.size === current.size ? current : next;
    });
  }, []);

  return { highlighted, previews, highlight, preview, clearPreviews };
}
